import { createSignal, onMount, Show } from "solid-js";
import { useDebugWebSocket } from "../hooks/useDebugWebSocket";
import { RealtimeFilterBar } from "./RealtimeFilterBar";
import { RealtimeEventsTable } from "./RealtimeEventsTable";
import { RealtimeConnectionsTable } from "./RealtimeConnectionsTable";
import { RealtimeStatsCards } from "./RealtimeStatsCards";
import { ConnectionDetail } from "./ConnectionDetail";
import { DebugSessionsList } from "./DebugSessionsList";
import { ProjectRealtimeDebugPanel } from "./ProjectRealtimeDebugPanel";
import { api } from "../shared";
import type { RealtimeConnectionInfo } from "../types";

function hashSubTab(): { tab: "live" | "sessions" | "debug"; projectId?: string } {
  const h = location.hash.slice(1);
  if (h === "realtime/sessions" || h.startsWith("realtime/sessions")) return { tab: "sessions" };
  if (h === "realtime/debug" || h === "realtime/debug/") return { tab: "debug" };
  if (h.startsWith("realtime/debug/")) {
    const projectId = h.slice("realtime/debug/".length);
    if (projectId) return { tab: "debug", projectId };
  }
  return { tab: "live" };
}

export function RealtimePanel() {
  const {
    events,
    allEvents,
    connections,
    stats,
    connected,
    error,
    connect,
    disconnect,
    updateFilters,
    setConnectionFilter,
    requestConnections,
    requestStats,
    clearEvents,
    clearError,
  } = useDebugWebSocket();

  const [selectedConnectionIds, setSelectedConnectionIds] = createSignal<Set<string>>(new Set());
  const [detailConnection, setDetailConnection] = createSignal<RealtimeConnectionInfo | null>(null);
  const [subTabInfo, setSubTabInfo] = createSignal(hashSubTab());
  const [debugProjectInput, setDebugProjectInput] = createSignal("");

  const [organizations, setOrganizations] = createSignal<Array<{ id: string; name: string; slug: string }>>([]);
  const [selectedOrgId, setSelectedOrgId] = createSignal("");
  const [orgProjects, setOrgProjects] = createSignal<Array<{ id: string; name: string; lifecycle: string; fullSlug?: string; memberCount?: number }>>([]);
  const [orgsLoading, setOrgsLoading] = createSignal(false);

  function goToProjectDebug() {
    const id = debugProjectInput().trim();
    if (id) location.hash = `realtime/debug/${id}`;
  }

  async function loadOrgs() {
    setOrgsLoading(true);
    try {
      const orgs = await api.organizations.list();
      setOrganizations(orgs);
    } catch (_) {}
    setOrgsLoading(false);
  }

  async function onOrgSelect(orgId: string) {
    setSelectedOrgId(orgId);
    setOrgProjects([]);
    if (!orgId) return;
    try {
      const projects = await api.organizations.projects(orgId);
      setOrgProjects(projects);
    } catch (_) {}
  }

  onMount(() => {
    disconnect();
    const onHash = () => setSubTabInfo(hashSubTab());
    window.addEventListener("hashchange", onHash);
    loadOrgs();
    return () => window.removeEventListener("hashchange", onHash);
  });

  function switchSubTab(t: "live" | "sessions") {
    setSubTabInfo({ tab: t });
    location.hash = t === "live" ? "realtime" : "realtime/sessions";
  }

  function handleToggleConnection(connId: string) {
    setSelectedConnectionIds((prev) => {
      const next = new Set(prev);
      if (next.has(connId)) next.delete(connId);
      else next.add(connId);
      setConnectionFilter(Array.from(next));
      return next;
    });
  }

  function handleClearConnections() {
    setSelectedConnectionIds(new Set());
    setConnectionFilter([]);
  }

  function handleConnect(filters: any, persist: boolean) {
    connect(filters, persist);
  }

  return (
    <div class="space-y-4">
      <div class="flex items-center gap-1 border-b border-gray-800 pb-0">
        <button
          onClick={() => switchSubTab("live")}
          class={`px-4 py-2 text-sm font-medium rounded-t cursor-pointer ${
            subTabInfo().tab === "live" ? "bg-gray-900 text-gray-200 border border-gray-800 border-b-gray-900" : "text-gray-500 hover:text-gray-300"
          }`}
        >
          Live
        </button>
        <button
          onClick={() => switchSubTab("sessions")}
          class={`px-4 py-2 text-sm font-medium rounded-t cursor-pointer ${
            subTabInfo().tab === "sessions" ? "bg-gray-900 text-gray-200 border border-gray-800 border-b-gray-900" : "text-gray-500 hover:text-gray-300"
          }`}
        >
          Sessions
        </button>
        <button
          onClick={() => {
            setSubTabInfo({ tab: "debug" });
            location.hash = "realtime/debug/";
          }}
          class={`px-4 py-2 text-sm font-medium rounded-t cursor-pointer ${
            subTabInfo().tab === "debug" ? "bg-gray-900 text-gray-200 border border-gray-800 border-b-gray-900" : "text-gray-500 hover:text-gray-300"
          }`}
        >
          Project Debug
        </button>
      </div>

      <Show when={subTabInfo().tab === "live"}>
        <div class="space-y-6">
          <Show when={error()}>
            <div class="bg-red-900/30 border border-red-700 text-red-300 px-4 py-3 rounded-lg text-sm flex items-center justify-between">
              <span>{error()}</span>
              <button onClick={clearError} class="text-gray-500 hover:text-gray-300 ml-4 cursor-pointer">x</button>
            </div>
          </Show>

          <RealtimeFilterBar
            connected={connected()}
            onConnect={handleConnect}
            onDisconnect={disconnect}
            onFilterUpdate={updateFilters}
            connections={connections()}
            selectedConnectionIds={Array.from(selectedConnectionIds())}
            onClearConnections={handleClearConnections}
          />

          <Show when={detailConnection()}>
            <ConnectionDetail
              connection={detailConnection()!}
              allEvents={allEvents()}
              onClose={() => setDetailConnection(null)}
            />
          </Show>

          <RealtimeStatsCards stats={stats()} />
          <RealtimeEventsTable events={events()} onClear={clearEvents} />

          <RealtimeConnectionsTable
            connections={connections()}
            selectedConnectionIds={selectedConnectionIds()}
            onToggleConnection={handleToggleConnection}
            onOpenDetail={(c) => setDetailConnection(c)}
            allEvents={allEvents()}
            onRefresh={() => { requestConnections(); requestStats(); }}
          />
        </div>
      </Show>

      <Show when={subTabInfo().tab === "sessions"}>
        <DebugSessionsList />
      </Show>

      <Show when={subTabInfo().tab === "debug" && !subTabInfo().projectId}>
        <div class="space-y-4">
          <div class="bg-gray-900 border border-gray-800 rounded-lg p-6">
            <h3 class="text-sm font-medium text-gray-300 mb-3">Project Realtime Inspector</h3>
            <p class="text-xs text-gray-500 mb-4">
              Select an organization and project to inspect its real-time connections, channel subscriptions, and routing events.
            </p>

            <div class="mb-4">
              <label class="block text-xs text-gray-500 mb-1">Organization</label>
              <select
                value={selectedOrgId()}
                onChange={(e) => onOrgSelect(e.target.value)}
                class="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-gray-200 cursor-pointer"
              >
                <option value="">{orgsLoading() ? "Loading..." : "Select an organization"}</option>
                {organizations().map((o) => (
                  <option value={o.id}>{o.name} ({o.slug})</option>
                ))}
              </select>
            </div>

            <Show when={orgProjects().length > 0}>
              <div class="bg-gray-800/50 border border-gray-700 rounded-lg overflow-hidden">
                <table class="w-full text-sm">
                  <thead>
                    <tr class="text-left text-gray-400 bg-gray-900/50">
                      <th class="px-3 py-2 font-medium">Project</th>
                      <th class="px-3 py-2 font-medium">Status</th>
                      <th class="px-3 py-2 font-medium">Members</th>
                      <th class="px-3 py-2 font-medium"></th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-gray-800/50">
                    {orgProjects().map((p) => (
                      <tr class="text-gray-300 hover:bg-gray-900/30">
                        <td class="px-3 py-2">
                          <div class="text-xs font-mono">{p.fullSlug || p.id}</div>
                          <div class="text-xs text-gray-500">{p.name}</div>
                        </td>
                        <td class="px-3 py-2">
                          <span class={`px-1.5 py-0.5 rounded text-xs font-medium ${
                            p.lifecycle === "active" ? "bg-green-900/50 text-green-300" : "bg-gray-800 text-gray-400"
                          }`}>{p.lifecycle}</span>
                        </td>
                        <td class="px-3 py-2 text-xs text-gray-400">{p.memberCount ?? 0}</td>
                        <td class="px-3 py-2">
                          <button
                            onClick={() => { location.hash = `realtime/debug/${p.id}`; }}
                            class="px-3 py-1 bg-cyan-700 text-white rounded text-xs font-medium hover:bg-cyan-600 cursor-pointer"
                          >
                            Debug
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Show>

            <div class="mt-4 pt-4 border-t border-gray-800">
              <p class="text-xs text-gray-500 mb-2">Or enter a project ID directly:</p>
              <div class="flex items-center gap-2">
                <input
                  type="text"
                  value={debugProjectInput()}
                  onInput={(e) => setDebugProjectInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") goToProjectDebug(); }}
                  placeholder="org2demo-prj3"
                  class="flex-1 px-3 py-2 bg-gray-800 border border-gray-700 rounded text-sm text-gray-200 placeholder-gray-500 outline-none focus:border-cyan-600"
                />
                <button
                  onClick={goToProjectDebug}
                  class="px-4 py-2 bg-cyan-600 text-white rounded text-sm font-medium hover:bg-cyan-500 cursor-pointer"
                >
                  Go
                </button>
              </div>
            </div>
          </div>
        </div>
      </Show>

      <Show when={subTabInfo().tab === "debug" && subTabInfo().projectId}>
        <ProjectRealtimeDebugPanel projectId={subTabInfo().projectId!} />
      </Show>
    </div>
  );
}
